import { INTERP_DELAY_MS, TICK_MS, lerpAngle, vLerp, type PlayerState, type ProjectileState, type ShipState } from "@dyson/shared";
import type { ShipPose } from "../render/ShipView";

/** About a second of snapshots kept for interpolation. */
const MAX_SNAPSHOTS = 24;

/** One server snapshot, timestamped on the server's clock (ms). */
interface Snap {
  time: number;
  players: Map<string, PlayerState>;
  ships: Map<string, ShipState>;
  projectiles: Map<string, ProjectileState>;
}

export interface Interpolated {
  players: PlayerState[];
  shipPose: Map<string, ShipPose>;
  projectiles: ProjectileState[];
}

/**
 * Recent server snapshots. Everyone but us is drawn INTERP_DELAY_MS in the
 * past, blended between the two snapshots either side of that moment, so
 * motion is smooth whatever the tick rate.
 */
export class Snapshots {
  private readonly snaps: Snap[] = [];
  /** Estimated server clock minus local clock, in ms. */
  private clockOffset?: number;

  reset(): void {
    this.snaps.length = 0;
    this.clockOffset = undefined;
  }

  record(tick: number, players: PlayerState[], ships: ShipState[], projectiles: ProjectileState[]): void {
    const time = tick * TICK_MS;
    // Track the server clock. Ease toward each sample so network jitter does not jerk the render time.
    const sample = time - performance.now();
    this.clockOffset = this.clockOffset === undefined ? sample : this.clockOffset + (sample - this.clockOffset) * 0.05;
    this.snaps.push({
      time,
      players: new Map(players.map((p) => [p.id, p])),
      ships: new Map(ships.map((s) => [s.id, s])),
      projectiles: new Map(projectiles.map((p) => [p.id, p])),
    });
    if (this.snaps.length > MAX_SNAPSHOTS) this.snaps.shift();
  }

  /** Everything as it was INTERP_DELAY_MS before `now` (local ms). */
  at(now: number): Interpolated {
    const empty = { players: [] as PlayerState[], shipPose: new Map<string, ShipPose>(), projectiles: [] as ProjectileState[] };
    const last = this.snaps[this.snaps.length - 1];
    if (!last || this.clockOffset === undefined) return empty;

    const renderTime = now + this.clockOffset - INTERP_DELAY_MS;
    let a = last;
    let b = last;
    for (let i = this.snaps.length - 1; i > 0; i--) {
      const prev = this.snaps[i - 1]!;
      if (prev.time <= renderTime) {
        a = prev;
        b = this.snaps[i]!;
        break;
      }
      a = b = prev; // render time is older than everything we kept
    }
    const t = b.time > a.time ? Math.min(1, Math.max(0, (renderTime - a.time) / (b.time - a.time))) : 1;

    const shipPose = new Map<string, ShipPose>();
    for (const [id, sb] of b.ships) {
      const sa = a.ships.get(id);
      shipPose.set(id, sa ? { position: vLerp(sa.position, sb.position, t), angle: lerpAngle(sa.angle, sb.angle, t), throttle: sb.throttle } : sb);
    }
    const players: PlayerState[] = [];
    for (const [id, pb] of b.players) {
      const pa = a.players.get(id);
      // Positions are relative to a ship while aboard; only blend within the same frame.
      players.push(
        pa && pa.frame === pb.frame
          ? { ...pb, position: vLerp(pa.position, pb.position, t), up: lerpAngle(pa.up, pb.up, t), aim: vLerp(pa.aim, pb.aim, t) }
          : pb,
      );
    }
    const projectiles: ProjectileState[] = [];
    for (const [id, qb] of b.projectiles) {
      const qa = a.projectiles.get(id);
      projectiles.push(qa ? { ...qb, position: vLerp(qa.position, qb.position, t) } : qb);
    }
    return { players, shipPose, projectiles };
  }
}
